import { doc, runTransaction, Transaction, DocumentReference } from 'firebase/firestore';
import { db } from './firebase';
import { Product, ProductSize } from '../types';
import { sanitizeFirestoreData } from '../utils/sanitizeFirestore';

/**
 * Calculates the total stock count across all sizes for a product.
 * Sizes array is the single source of truth for stock.
 */
export function getTotalStock(product?: { sizes?: ProductSize[] } | null): number {
  if (!product || !Array.isArray(product.sizes)) return 0;
  return product.sizes.reduce((sum, s) => sum + Math.max(0, Number(s.stock || 0)), 0);
}

/**
 * Retrieves the available stock count for a specific size.
 */
export function getSizeStock(product?: { sizes?: ProductSize[] } | null, sizeName?: string): number {
  if (!product || !Array.isArray(product.sizes) || !sizeName) return 0;
  const target = sizeName.trim().toLowerCase();
  const matched = product.sizes.find(
    (s) => s.size.trim().toLowerCase() === target
  );
  return matched ? Math.max(0, Number(matched.stock || 0)) : 0;
}

/**
 * Checks whether a product has any stock available.
 */
export function isProductInStock(product?: { sizes?: ProductSize[] } | null): boolean {
  return getTotalStock(product) > 0;
}

/**
 * Validates whether the requested quantity of a specific size is in stock.
 */
export function validateStockAvailability(
  product: Product,
  sizeName: string,
  requestedQty: number
): { valid: boolean; availableStock: number; error?: string } {
  const availableStock = getSizeStock(product, sizeName);
  if (requestedQty <= 0) {
    return { valid: false, availableStock, error: 'Quantity must be at least 1.' };
  }
  if (availableStock < requestedQty) {
    return {
      valid: false,
      availableStock,
      error: `Insufficient stock for "${product.name}" (${sizeName}). Available: ${availableStock}, Requested: ${requestedQty}.`
    };
  }
  return { valid: true, availableStock };
}

/**
 * Normalizes an array of product sizes, migrating legacy formats if necessary.
 */
export function normalizeProductSizes(rawSizes: any, fallbackSize?: string, fallbackQty?: number): ProductSize[] {
  if (Array.isArray(rawSizes) && rawSizes.length > 0) {
    return rawSizes.map((s: any, idx: number) => ({
      id: s.id || `sz-${idx}-${Date.now()}`,
      size: String(s.size || 'Standard').trim(),
      stock: Math.max(0, Number(s.stock ?? 0))
    }));
  }

  // Fallback migration for single-size items
  const defaultSizeLabel = String(fallbackSize || 'One Size').trim();
  const defaultStock = Math.max(0, Number(fallbackQty ?? 0));
  return [
    {
      id: `sz-default-${Date.now()}`,
      size: defaultSizeLabel,
      stock: defaultStock
    }
  ];
}

/**
 * Mutates a product document's sizes array in-memory for a Firestore transaction.
 * Returns the updated sizes array.
 */
export function applySizeDeltaToSizes(
  currentSizes: ProductSize[],
  targetSize: string,
  delta: number
): ProductSize[] {
  const target = targetSize.trim().toLowerCase();
  let found = false;

  const updatedSizes = currentSizes.map((s) => {
    if (s.size.trim().toLowerCase() === target) {
      found = true;
      const newStock = Math.max(0, Number(s.stock || 0) + delta);
      return { ...s, stock: newStock };
    }
    return s;
  });

  if (!found) {
    updatedSizes.push({
      id: `sz-${Date.now()}`,
      size: targetSize.trim(),
      stock: Math.max(0, delta)
    });
  }

  return updatedSizes;
}

/**
 * Transactional stock adjustment for a single product size (Admin or internal adjustment).
 * Executes inside a strict Firestore transaction.
 */
export async function adjustSizeStock(
  productId: string,
  targetSize: string,
  delta: number
): Promise<void> {
  await runTransaction(db, async (transaction) => {
    const prodRef = doc(db, 'products', productId);
    const prodSnap = await transaction.get(prodRef);
    if (!prodSnap.exists()) {
      throw new Error(`Product ${productId} does not exist.`);
    }

    const prodData = prodSnap.data() as Product;
    const currentSizes = normalizeProductSizes(prodData.sizes, prodData.size);
    const updatedSizes = applySizeDeltaToSizes(currentSizes, targetSize, delta);

    transaction.update(prodRef, sanitizeFirestoreData({
      sizes: updatedSizes,
      size: updatedSizes[0]?.size || 'Standard'
    }));
  });
}

/**
 * Sets the entire sizes array for a product atomically in Firestore.
 */
export async function setProductSizes(
  productId: string,
  sizes: ProductSize[]
): Promise<void> {
  await runTransaction(db, async (transaction) => {
    const prodRef = doc(db, 'products', productId);
    const prodSnap = await transaction.get(prodRef);
    if (!prodSnap.exists()) {
      throw new Error(`Product ${productId} does not exist.`);
    }

    const normalized = normalizeProductSizes(sizes);
    transaction.update(prodRef, sanitizeFirestoreData({
      sizes: normalized,
      size: normalized[0]?.size || 'Standard'
    }));
  });
}
